// Fresh Shopify sessions and explicitly confirmed, store-scoped editor grants cross this channel. Full Magnet credentials never do.
export const LINK_PROTOCOL = 'm5m-shopify-link-v1';
// A small display snapshot only. Never forward definitions, credentials or URLs.
export function setupSummary(value) {
  const text = v => typeof v === 'string' && v.length > 0 && v.length <= 240;
  const uuid = v => /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(v || '');
  if (!value || !text(value.workspace?.name) || !uuid(value.workspace?.guid) ||
      !Number.isSafeInteger(value.productCount) || value.productCount < 0 ||
      !['ready','stale','empty'].includes(value.catalogState) ||
      !Array.isArray(value.guides) || value.guides.length > 20 ||
      !value.guides.every(g => uuid(g.guid) && text(g.name) && ['published','draft','paused'].includes(g.status))) return null;
  return {workspace:{guid:value.workspace.guid,name:value.workspace.name}, productCount:value.productCount,
    catalogState:value.catalogState, guides:value.guides.map(({guid,name,status})=>({guid,name,status})),
    truncated:value.truncated === true};
}
export function editorAccess(value) {
  // This is transport validation, not authorization. A browser clock a few
  // seconds behind the issuer must not drop a newly issued ten-minute grant.
  // The API still verifies the signed expiry and current store/workspace scope.
  const now = Date.now(), clockSkew = 60000;
  return value && typeof value.token === 'string' && value.token.length <= 4096 &&
    /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value.token) && Number.isSafeInteger(value.expiresAt) &&
    (/^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(value.connectionGuid || '')) && value.expiresAt > now && value.expiresAt <= now + 10 * 60000 + clockSkew
    ? {token:value.token,expiresAt:value.expiresAt,connectionGuid:value.connectionGuid} : null;
}
export function linkContext(hash, allowedOrigins) {
  const params = new URLSearchParams(hash.split('?')[1] || '');
  const origin = params.get('origin'), nonce = params.get('nonce'), shop = params.get('shop');
  if (!allowedOrigins.includes(origin) || !/^[a-f0-9]{64}$/.test(nonce || '') ||
      !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop || '')) throw new Error('Start a new connection from the Shopify app.');
  return { origin, nonce, shop };
}
export function requestShopifySession({win=window, context, timeout=30000}) {
  return new Promise((resolve,reject)=>{
    if (!win.opener || win.opener.closed) return reject(new Error('The Shopify window is closed. Start a new connection there.'));
    const request = crypto.randomUUID();
    const cleanup=()=>{win.removeEventListener('message',receive);clearTimeout(timer);};
    const receive=event=>{
      const data=event.data;
      if(event.source!==win.opener || event.origin!==context.origin || data?.protocol!==LINK_PROTOCOL || data.nonce!==context.nonce || data.request!==request)return;
      if(data.type!=='session')return;
      cleanup();
      if(typeof data.token!=='string'||data.token.length>8192)return reject(new Error('Could not verify Shopify. Return to Shopify and try again.'));
      resolve(data.token);
    };
    const timer=setTimeout(()=>{cleanup();reject(new Error('Shopify did not respond. Return to its app and start again.'));},timeout);
    win.addEventListener('message',receive);
    win.opener.postMessage({protocol:LINK_PROTOCOL,type:'session-request',nonce:context.nonce,request},context.origin);
  });
}
/** Wait for the original Shopify app to confirm that it displayed the requested builder. */
export function openShopifyBuilder({win=window,context,summary,access,timeout=30000}) {
  return new Promise((resolve,reject)=>{
    if (!win.opener || win.opener.closed) return reject(new Error('The Shopify window is closed. Start again from Shopify.'));
    const request=crypto.randomUUID();
    const cleanup=()=>{win.removeEventListener('message',receive);clearTimeout(timer);};
    const receive=event=>{
      const data=event.data;
      if(event.source!==win.opener || event.origin!==context.origin || data?.protocol!==LINK_PROTOCOL || data.nonce!==context.nonce || data.request!==request || data.type!=='builder-result')return;
      cleanup();
      if(!['editor','guides','renewed'].includes(data.destination))return reject(new Error('The builder could not open. Return to Shopify and refresh setup.'));
      resolve(data.destination);
    };
    const timer=setTimeout(()=>{cleanup();reject(new Error('Shopify did not confirm the builder opened. Refresh the Shopify app and try again.'));},timeout);
    win.addEventListener('message',receive);
    win.opener.postMessage({protocol:LINK_PROTOCOL,type:'setup',nonce:context.nonce,shop:context.shop,summary,editorAccess:access,openBuilder:true,request},context.origin);
  });
}
export function openWorkspaceLink({win=window,url,shop,idToken,onStatus,timeout=10*60000}) {
  const target=new URL(url), nonce=Array.from(crypto.getRandomValues(new Uint8Array(32)),b=>b.toString(16).padStart(2,'0')).join('');
  if(target.protocol!=='https:' && !(['localhost','127.0.0.1'].includes(target.hostname)&&target.protocol==='http:'))throw new Error('Invalid workspace connection URL.');
  // A hash-only navigation can reuse stale dashboard HTML after a release.
  // This independent cache key contains no authentication or channel data.
  target.searchParams.set('shopifySetup', crypto.randomUUID());
  target.hash=`/shopify/connect?${new URLSearchParams({origin:win.location.origin,nonce,shop})}`;
  const popup=win.open(target.href,'_blank');
  if(!popup)throw new Error('Allow a new window to connect your Magnet workspace.');
  let active=true,completed=false;
  const stop=()=>{active=false;win.removeEventListener('message',receive);clearTimeout(timer);};
  const receive=async event=>{
    const data=event.data;
    if(!active||popup.closed||event.source!==popup||event.origin!==target.origin||data?.protocol!==LINK_PROTOCOL||data.nonce!==nonce)return;
    if(data.type==='session-request'&&typeof data.request==='string'&&data.request.length<100){
      let token=null;try{token=await idToken();}catch{}
      if(active&&!popup.closed)popup.postMessage({protocol:LINK_PROTOCOL,type:'session',nonce,request:data.request,token},target.origin);
    }else if(data.type==='setup'&&data.shop===shop){
      const summary=setupSummary(data.summary);
      if(summary){
        completed=true;
        const openBuilder=data.openBuilder===true && typeof data.request==='string' && data.request.length<100;
        let result;
        try { result=await onStatus({type:'setup',shop,summary,editorAccess:editorAccess(data.editorAccess),...(openBuilder?{openBuilder:true}:{})}); } catch {}
        if(openBuilder && active && !popup.closed)popup.postMessage({protocol:LINK_PROTOCOL,type:'builder-result',nonce,request:data.request,destination:['editor','guides','renewed'].includes(result?.destination)?result.destination:null},target.origin);
      }
    }else if(['connected','disconnected'].includes(data.type)&&data.shop===shop){completed=true;onStatus(data);}
  };
  const timer=setTimeout(()=>{stop();if(!completed)onStatus({type:'expired'});},timeout);
  win.addEventListener('message',receive);
  return stop;
}
